import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Dialog, DialogContent } from "@mui/material";

import { useGetShippingMethods } from "../../../hooks/shipping/useGetShippingMethods";
import { useDeleteShippingMethod } from "../../../hooks/shipping/useDeleteShippingMethod";
import { useUpdateShippingMethod } from "../../../hooks/shipping/useUpdateShippingMethod";
import useTableActions from "../../../hooks/common/useTableActions";

import TableLayout from "../../global/Table/TableLayout";
import { ShippingMethodsTableColumns } from "./Table/ShippingMethodsTableColumns";
import ShippingMethodsTableRow from "./Table/ShippingMethodsTableRow";
import FormAddEditShippingMethod from "./FormAddEditShippingMethod";

const ShippingMethodsTable = () => {
    const { i18n } = useTranslation();
    const isArabic = (i18n.language || "en").startsWith("ar");

    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingItem, setEditingItem] = useState(null);

    const { deleteShippingMethodMutate, isDeleting } = useDeleteShippingMethod();
    const { updateShippingMethodMutate } = useUpdateShippingMethod();

    const {
        page,
        perPage,
        search,
        order,
        selectedIds,
        menuAnchorEl,
        selectedItem,
        setSearch,
        handleRequestSort,
        handleRowCheckboxClick,
        handleSelectAllClick,
        handleMenuOpen,
        handleMenuClose,
        handleMenuDelete,
        handlePageChange,
        handlePerPageChange,
    } = useTableActions({
        basePath: "/shipping",
        deleteSingle: deleteShippingMethodMutate,
    });

    const params = useMemo(() => ({
        page,
        per_page: perPage,
        search: search || undefined,
        sort_by: "created_at",
        sort_order: order,
    }), [page, perPage, search, order]);

    const { methods, meta, isLoading, error, refetch } = useGetShippingMethods(params);

    const openCreate = () => {
        setEditingItem(null);
        setDialogOpen(true);
    };

    const openEdit = () => {
        if (!selectedItem) return;
        setEditingItem(selectedItem);
        setDialogOpen(true);
        handleMenuClose();
    };

    const closeDialog = () => {
        setDialogOpen(false);
        setEditingItem(null);
    };

    const handleToggleActive = (item) => {
        updateShippingMethodMutate({
            id: item.id,
            data: { is_active: !item.is_active },
        });
    };

    const renderRow = (rowParams) => (
        <ShippingMethodsTableRow
            key={rowParams.item.id}
            item={rowParams.item}
            index={rowParams.index}
            isSelected={selectedIds.includes(rowParams.item.id)}
            onRowCheckboxClick={handleRowCheckboxClick}
            onMenuOpen={handleMenuOpen}
            onToggleActive={handleToggleActive}
        />
    );

    return (
        <>
            <TableLayout
                title={isArabic ? "طرق الشحن" : "Shipping Methods"}
                subtitle={isArabic ? "إدارة طرق الشحن المتاحة وتسعيرها" : "Manage available shipping methods and pricing"}
                search={search}
                onSearchChange={setSearch}
                addButtonLabel={isArabic ? "إضافة طريقة" : "Add Method"}
                onAddClick={openCreate}
                isLoading={isLoading}
                error={error}
                items={methods}
                meta={meta}
                page={page}
                perPage={perPage}
                onPageChange={handlePageChange}
                onPerPageChange={handlePerPageChange}
                order={order}
                onRequestSort={handleRequestSort}
                columns={ShippingMethodsTableColumns}
                renderRow={renderRow}
                menuAnchorEl={menuAnchorEl}
                openMenu={Boolean(menuAnchorEl)}
                onMenuOpen={handleMenuOpen}
                onMenuClose={handleMenuClose}
                onMenuEdit={openEdit}
                onMenuDelete={handleMenuDelete}
                isDeletingSingle={isDeleting}
                selectedIds={selectedIds}
                onSelectAllClick={(e) => handleSelectAllClick(e, methods)}
                onRowCheckboxClick={handleRowCheckboxClick}
                bulkDeleteDisabled
                emptyLabelAr="لا توجد طرق شحن"
                emptyLabelEn="No shipping methods"
                refetch={refetch}
            />

            <Dialog open={dialogOpen} onClose={closeDialog} fullWidth maxWidth="md">
                <DialogContent sx={{ p: { xs: 2, md: 4 } }}>
                    <FormAddEditShippingMethod
                        isEdit={!!editingItem}
                        currentData={editingItem}
                        onSubmitSuccess={closeDialog}
                    />
                </DialogContent>
            </Dialog>
        </>
    );
};

export default ShippingMethodsTable;
